import {api} from "@/shared/api";

import {BasicDataTable, BasicDataTableConfig, DataTableResponse, TypedColumnSettings} from "@/shared/ui/basicDataTable"
import {TerminalDialog} from "@/features/terminal/terminalDialog";
import {AddTerminalDialog} from "@/features/terminal/addTerminalDialog";
import * as Constants from "@/shared/constants";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {TerminalConfig, WorkingTimeType} from "@/shared/timr";

export interface TerminalsPageConfig {
	minutesBasedWorkingTimeTypes: WorkingTimeType[];
}

export class TerminalsAdministrationPage {
	private readonly addTerminalDialog: AddTerminalDialog;
	private readonly terminalDialog: TerminalDialog;
	private readonly terminalsTable: TerminalsTable;

	constructor(public config: TerminalsPageConfig) {
		this.addTerminalDialog = new AddTerminalDialog({minuteBasedWorkingTimeTypes: config.minutesBasedWorkingTimeTypes});
		this.terminalsTable = new TerminalsTable({
			element: $("#terminalsTable"),
			orderable: false,
			allowExpand: true,
			clampNote: true,
		});
		this.terminalDialog = new TerminalDialog({minuteBasedWorkingTimeTypes: config.minutesBasedWorkingTimeTypes});

		$(".addTerminalButton").click(() => this.addTerminalDialog.open(null));
		$(this.terminalsTable).on(BasicDataTable.EVENT_DBLCLICK + " " + TerminalsTable.EVENT_EDIT_TERMINAL, (e, terminal: TerminalConfig) => this.terminalDialog.open(terminal));
		$(this.terminalsTable).on(BasicDataTable.EVENT_DATA_CHANGED, (e, terminal: DataTableResponse<TerminalConfig>) => {
			const terminalsAvailable = terminal.recordsTotal > 0;
			$("#noConnectedTerminalInfoPanel").toggle(!terminalsAvailable);
			$("#terminalsTableContainerPanel").toggle(terminalsAvailable);
		});
		$(this.terminalDialog).on(TerminalDialog.EVENT_SAVED_TERMINAL, () => this.terminalsTable.reload());
		$(this.addTerminalDialog).on(AddTerminalDialog.EVENT_ADDED_TERMINAL, () => this.terminalsTable.reload());
		$(this.addTerminalDialog).on(AddTerminalDialog.EVENT_CLOSED, () => this.terminalsTable.reload());

		$(this.terminalsTable).on(
			TerminalsTable.EVENT_RECONNECT_TERMINAL,
			(e, terminal: TerminalConfig) => this.addTerminalDialog.open(terminal)
		);
	}
}

class TerminalsTable extends BasicDataTable<TerminalConfig> {
	public static EVENT_EDIT_TERMINAL = "event_edit_terminal";
	public static EVENT_RECONNECT_TERMINAL = "event_reconnect_terminal";
	public static EVENT_REMOVE_TERMINAL = "event_remove_terminal";

	constructor(config: BasicDataTableConfig) {
		super("/timr/admin/getTerminalConfigs.json", config);
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt<"datatable-footer"ip>`,
			paging: true,
			serverSide: true,
			pageLength: Constants.ADMINISTRATION_MAIN_TABLES_PAGE_LENGTH
		};
	}

	protected getColumnSettings(): TypedColumnSettings<TerminalConfig>[] {
		return [
			{data: "id", width: "44px", orderable: false, className: "context-menu", render: this.contextMenu.render},
			{data: "name", orderable: true, render: BasicDataTable.renderText}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[1, "asc"]];
	}

	protected createContextMenu() {
		return super.createContextMenu()
			.addMenuEntry({
				label: "terminals.context.edit",
				action: terminal => { $(this).triggerHandler(TerminalsTable.EVENT_EDIT_TERMINAL, terminal); }
			})
			.addMenuEntry({
				label: "terminals.context.reconnect",
				action: terminal => this.reconnectTerminalDialog(() => {
						$(this).triggerHandler(TerminalsTable.EVENT_RECONNECT_TERMINAL, terminal);
					}
				)
			})
			.addMenuEntry({
				label: "terminals.context.remove",
				action: terminal => this.removeTerminal(terminal.id)
			});
	}

	private removeTerminal(terminalId: number) {
		BootstrapModal.confirm({
			title: "terminals.removeWarning.title",
			message: "terminals.removeWarning.message",
			yesLabel: "confirmation.delete",
			noLabel: "dialog.cancel",
			yesAction: (modal) => {
				modal.close();
				api("admin/deleteTerminalConfig.json")
					.post({
						terminalId: terminalId
					}).done(() => {
					this.reload();
				});
			}
		});
	}

	private reconnectTerminalDialog(onYesAction: () => void) {
		BootstrapModal.confirm({
			title: "terminals.reconnectWarning.title",
			message: "terminals.reconnectWarning.message",
			yesLabel: "confirmation.reconnect",
			noLabel: "dialog.cancel",
			yesAction: (modal) => {
				modal.close();
				onYesAction();
			}
		});
	}
}
